import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import enginesService from '../services/enginesService';
import {
  Database, Users, Building2, CreditCard,
  FileText, MessageSquare, Workflow, Plug, FileSignature, KeyRound
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';
import useSmsUnread from '../hooks/useSmsUnread';
import CommunicationSection from './CommunicationSection';
import ThirdPartyAppsSection from './ThirdPartyAppsSection';

// Communication is now embedded as a Settings tab (2026-06-01) — the
// old top-level /communication page was moved here so there is exactly
// ONE place for sender/AIVA config + bulk send. Automation stays on its
// own left-nav page (operator-configurable triggers).
const TABS = [
  { key: 'facility', label: 'Facility', icon: Building2,
    desc: 'Core facility profile: address, hours, branding' },
  { key: 'payments', label: 'Payments', icon: CreditCard,
    desc: 'Stripe Connect, tax rates, fees, promotions' },
  { key: 'documents', label: 'Documents', icon: FileText,
    desc: 'Lease templates and e-signature sends' },
  // Automation folded in here 2026-07-19: it IS tenant communication, just
  // the half the system sends by itself. Splitting "the system emails this"
  // from "I email this" across two top-level tabs meant no single place
  // answered "what does a tenant hear from us?".
  { key: 'communication', label: 'Communication', icon: MessageSquare,
    desc: 'Everything tenants hear from you — automated rules and manual sends' },
  { key: 'apps', label: 'Third-party apps', icon: Plug,
    desc: 'Connect external tools like QuickBooks Online' },
];

const Card = ({ title, description, icon: Icon, path, color = 'blue', external, addOn, cta }) => {
  // All Settings tile icons follow the active brand theme. The `color`
  // prop is preserved on the call sites for back-compat / future
  // semantic use but currently every card resolves to the brand palette
  // so green/orange theme switches re-color every tile uniformly.
  const c = { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'hover:border-blue-200' };
  const inner = (
    <>
      <div className={`h-9 w-9 ${c.bg} rounded-lg flex items-center justify-center mb-3`}>
        <Icon className={`h-4 w-4 ${c.text}`} />
      </div>
      <h3 className="text-[15px] font-semibold text-slate-900 mb-1 flex items-center gap-2">
        {title}
        {addOn && (
          <span className="rounded-full border border-blue-200 bg-blue-50 px-2 text-[11px] font-medium leading-[18px] text-blue-700">
            Add-on
          </span>
        )}
      </h3>
      <p className="text-[13px] text-slate-400 mb-3">{description}</p>
      <span className={`text-[13px] font-medium ${c.text}`}>{cta || 'Configure'} &rarr;</span>
    </>
  );
  if (external || !path) {
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white p-5 opacity-60 cursor-not-allowed">
        <div className={`h-9 w-9 ${c.bg} rounded-lg flex items-center justify-center mb-3`}>
          <Icon className={`h-4 w-4 ${c.text}`} />
        </div>
        <h3 className="text-[15px] font-semibold text-slate-900 mb-1">{title}</h3>
        <p className="text-[13px] text-slate-400 mb-3">{description}</p>
        <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Coming Soon</span>
      </div>
    );
  }
  return (
    <Link
      to={path}
      className={`rounded-xl border border-slate-200/80 bg-white p-5 ${c.ring} hover:shadow-sm transition-all block`}
    >{inner}</Link>
  );
};

const Settings = () => {
  const { currentFacility } = useFacility();
  const { isPortfolioScoped, isPlatformUser } = useAuth();
  // Only org-wide roles can add a facility to the portfolio.
  const canSetOrgPickerControls = isPortfolioScoped() || isPlatformUser();
  const smsUnread = useSmsUnread(currentFacility?._id);
  const [signingConnected, setSigningConnected] = useState(null);
  useEffect(() => {
    const fid = currentFacility?._id;
    if (!fid) return undefined;
    let active = true;
    setSigningConnected(null);
    enginesService.connection('esign', fid)
      .then((res) => { if (active) setSigningConnected(!!res.data?.connected); })
      .catch(() => { if (active) setSigningConnected(null); });
    return () => { active = false; };
  }, [currentFacility?._id]);
  // Tab is URL-driven so /settings?tab=communication deep-links work and
  // the legacy /communication route can redirect cleanly.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // Unknown or retired tabs fall back to Facility. That covers old
  // ?tab=users links for free — Users was folded into Facility, which is
  // exactly where its two tiles now live.
  // 'automation' now lives inside Communication; old links land there.
  const tab = rawTab === 'automation'
    ? 'communication'
    : (TABS.find((t) => t.key === rawTab) ? rawTab : 'facility');
  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'facility') params.delete('tab');
    else params.set('tab', key);
    // Switching tabs in Settings drops any sub-tab `view=...` from the
    // previously-active embedded section (e.g. Communication's text/settings
    // sub-tab). Otherwise leaving Communication for Facility would carry the
    // stale ?view= around.
    params.delete('view');
    setSearchParams(params, { replace: true });
  };

  const groups = {
    facility: [
      // "Add a Facility" leads: onboarding a new site is the thing you come
      // here to DO, editing an existing one is routine. Portfolio-scoped
      // roles only — a facility manager has nowhere to add one to.
      ...(currentFacility && canSetOrgPickerControls ? [{
        title: 'Add a Facility',
        description: 'Onboard another site — new build or one you just acquired',
        icon: Building2, path: '/settings/facility/new', color: 'green',
      }] : []),
      // Tile order is the owner's (2026-08-07): row 1 = Add / Facility
      // Settings / Snapshot Export, row 2 = Users & Roles / Custom
      // Fields / Gate Controllers.
      {
        title: currentFacility ? 'Facility Settings' : 'Create Facility',
        description: currentFacility
          ? `Edit ${currentFacility.name}'s address, tax rate, late fees`
          : 'Set up your storage facility to start managing units',
        icon: Building2, path: '/settings/facility', color: 'blue',
      },
      // Moved here from Documents (owner, 2026-08-07): facility data,
      // not a document — a TILE on this tab, not a FacilityForm tab.
      {
        title: 'Facility Export',
        description: 'Download current customers + rentals + units as one denormalized CSV — plus the full data export',
        icon: FileText, path: '/settings/portfolio-export', color: 'green',
      },
      // Users had its own top-level tab for two tiles. Both are facility
      // configuration — who can sign in here, and viewing a tenant of this
      // facility — so they live with the rest of it rather than behind a
      // tab you had to know to open.
      {
        // Tenant Impersonation folded INTO Users & Roles as a tab
        // (owner, 2026-08-06) — one people-and-access surface.
        title: 'Users & Roles',
        description: 'Invite facility managers, staff, viewers — plus tenant impersonation for phone support',
        icon: Users, path: '/settings/users', color: 'green',
      },
      {
        title: 'Custom Fields',
        description: 'Add custom fields for your facility — extra tenant details you want to collect',
        icon: Database, path: '/settings/custom-fields', color: 'blue',
      },
      // Gate config moved out of Automation: it is facility hardware setup
      // (which controller, code length, code strategy), not a notification
      // rule. The automation it drives — minting a code at move-in — is the
      // same lifecycle every other facility setting shapes.
      {
        title: 'Gate Controllers',
        description: 'Gate vendor, access codes and how each tenant’s code is generated',
        icon: KeyRound, path: '/settings/gate', color: 'blue',
      },
    ],
    payments: [
      {
        title: 'Stripe Connect',
        description: 'Connect your Stripe account so tenants can pay online',
        icon: CreditCard, path: '/settings/stripe', color: 'purple',
      },
      {
        title: 'Tax Rates',
        description: 'Named tax rates this facility charges (one default)',
        icon: Database, path: '/settings/tax-rates', color: 'green',
      },
      {
        // Combined page: Fee Catalog + Late/Lien live as two internal tabs
        // under /settings/fees now. Promotions moved to the Units nav
        // (/units?view=promotions) 2026-06-19.
        title: 'Fees',
        description: 'Fee catalog plus automatic late-fee tiers and grace period',
        icon: Database, path: '/settings/fees', color: 'blue',
      },
    ],
    documents: [
      {
        title: 'Storage Agreements',
        description: 'Upload DOCX lease templates with merge tags; pin a facility default',
        icon: FileText, path: '/settings/storage-agreements', color: 'blue',
      },
      // A paid add-on, connected per facility. Always listed: connected, it
      // opens the signing screen; not connected, the same page says it is
      // an add-on and where to switch it on. Unknown status shows no label.
      {
        title: 'Document signing',
        description: signingConnected
          ? 'Send any document for electronic signature and track it to completion'
          : 'Send documents for electronic signature — available as an add-on',
        icon: FileSignature, path: '/document-signing', color: 'blue',
        addOn: signingConnected === false,
        cta: signingConnected ? 'Open' : 'Learn more',
      },
      // Gate-Code Card tile removed 2026-06-01 -- mySMS360 doesn't print
      // anything; gate codes ship via the consolidated welcome email.
      // See [[project_consolidated_email_flow]].
      // Facility Snapshot Export tile moved to the Facility page
      // (owner, 2026-08-06) — it is facility data, not a document.
      // E-signature is leaving the product as a paid service, so it is no
      // longer offered to a facility that has not already got it (owner,
      // 2026-09-20). `esign_enabled` is false for every NEW facility, which
      // is the whole point: a prospect must not find signing sitting here
      // looking free. Facilities already using it keep it, unchanged, and
      // History keeps serving every document ever signed either way — the
      // flag hides the two entry points, it does not withdraw anything.
      ...(currentFacility?.esign_enabled ? [{
        title: 'Manual Lien eSign',
        description: 'Fill a lien notice by hand (RBT / NOC / NPS) and send it for e-signature — for facilities not managed in mySMS360',
        icon: FileSignature, path: '/manual-esign', color: 'purple',
      }, {
        // General-purpose e-signature (owner: no lien wording — this is
        // a DocuSign-style feature, nothing lien-specific).
        title: 'Send any PDF for eSign',
        description: 'Upload any document (Word converts to PDF) and send it for e-signature',
        icon: FileSignature, path: '/send-esign', color: 'green',
      }] : []),
      // Lien / Auction Notice tile removed 2026-06-05 -- the lien
      // surface now lives at /auctions (left-nav). Documents tab keeps
      // operator-facing template config only.
    ],
  };
  const active = TABS.find((t) => t.key === tab);
  const isEmbedded = tab === 'communication' || tab === 'automation' || tab === 'apps';
  const cards = groups[tab] || [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">Manage system settings and configurations</p>
      </div>

      <div className="border-b border-slate-200 mb-5 overflow-x-auto">
        <nav className="flex gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === tab;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.key === 'communication' && smsUnread > 0 && (
                  <span
                    className="flex items-center justify-center rounded-full text-white font-semibold"
                    style={{ minWidth: 17, height: 17, fontSize: 10, padding: '0 5px', background: '#DC2626' }}
                  >
                    {smsUnread > 99 ? '99+' : smsUnread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {active?.desc && !isEmbedded && (
        <p className="text-[12px] text-slate-400 mb-4">{active.desc}</p>
      )}

      {tab === 'communication' ? (
        <CommunicationSection embedded />
      ) : tab === 'apps' ? (
        <ThirdPartyAppsSection embedded />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map((card, i) => (
              <Card key={card.path || `${tab}-${i}`} {...card} />
            ))}
          </div>

          <div className="mt-6 rounded-xl border border-amber-200/80 bg-amber-50/50 p-4">
            <p className="text-[13px] text-amber-700">
              <span className="font-medium">Administrator Access:</span> These settings require administrator or manager privileges. Changes may affect system-wide behavior.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Settings;
